/**
 * O QUE JÁ FOI FEITO AQUI — a prateleira real, na home.
 *
 * ── O PONTO (`T6.7`) ─────────────────────────────────────────────────────────────────────────
 *
 * Ele, na letra: *"Eu faria mais coisa na página inicial. A base inicial tem que ser muito
 * marcante. **Ela tá sendo muito simples.** Tem mais flair, mais interesse, mais vontade de
 * inspirar **a vontade de participar**. Um pouco mais de dopamina no uso (…) Tem que dar uma
 * pesquisada aqui do que pode ser feito."*
 *
 * ── A PESQUISADA, QUE É INSTRUÇÃO DELE, E O QUE ELA ACHOU ────────────────────────────────────
 *
 * A home tem **duas** seções: o herói e os quatro passos. Cento e setenta e nove linhas. O "muito
 * simples" dele é literal.
 *
 * E o que ela mostra no herói é uma **prévia falsa**: quatro retângulos de cor fixa no código
 * (`background: "#CFE8A8"`) e um balão inventado que diz *"Vamos investigar!"*. Enquanto isso, a
 * plataforma tem uma biblioteca com quadrinhos publicados de verdade — e a ficha de cada um é
 * **pública por desenho**, sem conta: o comentário de `firestore.rules` diz que *"a ficha continua
 * pública de propósito: ela É o preview"*.
 *
 * Então a resposta mais substantiva à "vontade de participar" não é mais desenho meu: é **mostrar
 * quem já participou**. Nome, título e escola de crianças reais pesam mais que qualquer retângulo
 * colorido que eu consiga inventar.
 *
 * ── O QUE ESTA PRATELEIRA **NÃO** MOSTRA, E POR QUE ISSO NÃO É DESCUIDO ──────────────────────
 *
 * **Não mostra imagem.** A ficha pública não tem capa, e acrescentar uma mudaria o que este
 * produto expõe sem conta — que é decisão tomada de propósito no `T2.25` e no `T2.63`, com três
 * estados de visibilidade desenhados um por um. Pôr a primeira imagem do quadrinho num documento
 * legível por qualquer um é exatamente o que aquelas regras evitam.
 *
 * Isso virou pergunta ao operador no `PL-T6.4`, não escolha minha.
 */
import Firebase from "../../api/firebase.class";
import { ehResiduoDeTeste } from "../../api/residuo-de-teste";

/** Um quadrinho na prateleira, com só o que a ficha pública carrega. */
export interface QuadrinhoNaPrateleira {
  id: string;
  titulo: string;
  autor: string;
  escola?: string;
  paginas?: number;
  /** A capa, quando quem publicou consentiu. Sem ela o cartao sai como sempre saiu. */
  capa?: string;
}

/** Quantos cabem. Seis: três por fileira em tela larga, e uma fileira só em telas estreitas. */
export const QUANTOS_NA_PRATELEIRA = 6;

/**
 * O que veio do Firestore, reduzido ao que a tela desenha.
 *
 * Separado da leitura para poder ser testado sem rede — e porque este é o pedaço que erra: o campo
 * se chama `turma` e guarda a ESCOLA (o comentário de `converter.ts` explica por que o nome ficou),
 * e `autor` é objeto e não string.
 *
 * Documento sem título ou sem autor é DESCARTADO, e não mostrado com "(sem título)": a home é a
 * primeira coisa que alguém vê, e um cartão quebrado nela diz mais sobre o produto que o quadrinho
 * que ele deveria anunciar.
 */
export function daFichaPublica(brutos: unknown[]): QuadrinhoNaPrateleira[] {
  const prateleira: QuadrinhoNaPrateleira[] = [];

  for (const bruto of brutos) {
    const d = bruto as {
      id?: unknown;
      titulo?: unknown;
      autor?: { nome?: unknown; turma?: unknown };
      quantasPaginas?: unknown;
      capa?: unknown;
    };
    if (typeof d?.id !== "string" || !d.id) continue;
    if (typeof d?.titulo !== "string" || !d.titulo.trim()) continue;
    // T30.9 · nem o que quem testa marcou como teste.
    if (ehResiduoDeTeste(d.titulo)) continue;
    const autor = typeof d.autor?.nome === "string" ? d.autor.nome.trim() : "";
    if (!autor) continue;

    prateleira.push({
      id: d.id,
      titulo: d.titulo.trim(),
      autor,
      escola: typeof d.autor?.turma === "string" && d.autor.turma.trim() ? d.autor.turma.trim() : undefined,
      paginas: typeof d.quantasPaginas === "number" && d.quantasPaginas > 0 ? d.quantasPaginas : undefined,
      // So `data:image`. Uma ficha e escrita por quem publica, e a home a le sem conta: aceitar
      // qualquer string aqui poria um endereco de terceiro dentro de um `img` da nossa home.
      capa:
        typeof d.capa === "string" && d.capa.startsWith("data:image") ? d.capa : undefined,
    });
  }

  return prateleira;
}

/**
 * Quantas páginas, escrito como gente fala.
 *
 * "1 páginas" numa home é o tipo de erro que faz a pessoa duvidar do resto da tela.
 */
export function quantasPaginas(n?: number): string | null {
  if (!n || n < 1) return null;
  return n === 1 ? "1 página" : `${n} páginas`;
}

/**
 * Busca a prateleira.
 *
 * **Nunca lança.** Esta é a home: se a biblioteca estiver fora do ar, a página tem que abrir do
 * mesmo jeito, sem seção nenhuma, em vez de mostrar um erro para quem ainda não sabe o que este
 * produto é. Lista vazia é o estado de falha E o estado de "ainda não tem nada publicado", e a tela
 * trata os dois igual porque para quem chega eles são iguais.
 *
 * E o `where` vai na CONSULTA, não na filtragem depois: a regra do Firestore avalia a consulta, e
 * uma listagem sem ele devolve `permission-denied` para todo mundo. Isso está medido no cabeçalho
 * de `pages/biblioteca/biblioteca.tsx`, e foi achado por validação independente antes de as regras
 * subirem.
 */
export async function buscarPrateleira(
  buscar: (
    caminho: string,
    filtros: unknown[],
    limite?: number
  ) => Promise<unknown[]> = Firebase.getCollection as never,
  onde: unknown[] = []
): Promise<QuadrinhoNaPrateleira[]> {
  try {
    const brutos = await buscar("biblioteca", onde, QUANTOS_NA_PRATELEIRA);
    return daFichaPublica(brutos);
  } catch (e) {
    console.warn("[inicio] não consegui buscar a prateleira; a home abre sem ela", e);
    return [];
  }
}
